﻿<script lang="ts" setup>
import TheCheckbox from '@/components/atoms/TheCheckbox/TheCheckbox.vue'
import InputFieldHeader from '@/components/atoms/InputFieldHeader/InputFieldHeader.vue'
import { Loader } from '@googlemaps/js-api-loader'
import { computed, onMounted, ref, toRef, watch } from 'vue'
import { useDebounceFn } from '@vueuse/core'

const props = withDefaults(
    defineProps<{
        latitude?: number
        longitude?: number
        heading?: number
        pitch?: number
        zoom?: number
        showStreetView?: boolean
        disableSwitching?: boolean
        preventMovement?: boolean
        label?: string
        type?: string
        required?: boolean
        helpText?: string
        placeholder?: string
    }>(),
    {
        showStreetView: false,
        disableSwitching: false,
        preventMovement: false
    }
)
const emit = defineEmits<{
    (e: 'update:pitch', pitch?: number): void
    (e: 'update:heading', heading?: number): void
    (e: 'update:zoom', zoom?: number): void
    (e: 'update:latitude', lat?: number): void
    (e: 'update:longitude', long?: number): void
    (e: 'update:showStreetView', value: boolean): void
}>()

const showStreetView = computed({
    get: () => props.showStreetView,
    set(value: boolean) {
        emit('update:showStreetView', value)
    }
})

const loader = ref<Loader | null>(null)
onMounted(() => {
    if (props.showStreetView) {
        loader.value = new Loader({
            apiKey: 'AIzaSyBmL2NKoyJPNSV8yllyapSCXgPcH-mXoio',
            version: 'weekly'
        })
        loader.value.load().then(initialize)
    }
})
const watcher1 = watch(
    () => showStreetView.value,
    () => {
        if (loader.value === null) {
            loader.value = new Loader({
                apiKey: 'AIzaSyBmL2NKoyJPNSV8yllyapSCXgPcH-mXoio',
                version: 'weekly'
            })
            loader.value.load().then(initialize)
        }
    }
)

const _lat = toRef(props, 'latitude')
const _long = toRef(props, 'longitude')
const watcher2 = watch([_lat, _long], ([lat, long]) => {
    if (streetView.value) {
        const currentPosition = streetView.value.getPosition()
        const position = {
            lat: lat ?? (currentPosition?.lat() as number),
            lng: long ?? (currentPosition?.lng() as number)
        }
        streetView.value.setPosition(position)
    }
})

const streetView = ref<google.maps.StreetViewPanorama | null>(null)
const panoRef = ref<HTMLElement | null>()

function initialize() {
    streetView.value = new google.maps.StreetViewPanorama(panoRef.value as HTMLElement, {
        position: { lat: props.latitude ?? 55.71071790800002, lng: props.longitude ?? 12.572880922359447 },
        addressControl: false,
        clickToGo: !props.preventMovement,
        zoomControl: props.preventMovement,
        panControl: props.preventMovement,
        disableDefaultUI: props.preventMovement,
        fullscreenControl: props.preventMovement,
        pov: {
            pitch: props.pitch ?? 0,
            heading: props.heading ?? 0
        },
        zoom: props.zoom ?? 1
    })
    streetView.value.addListener('position_changed', () => {
        emit('update:longitude', streetView.value?.getPosition()?.lng())
        emit('update:latitude', streetView.value?.getPosition()?.lat())
    })
    streetView.value.addListener(
        'pov_changed',
        useDebounceFn(() => {
            const pov = streetView.value?.getPov()
            const zoom = streetView.value?.getZoom()
            emit('update:pitch', pov?.pitch)
            emit('update:heading', pov?.heading)
            emit('update:zoom', zoom)
        }, 500)
    )
}
</script>

<template>
    <div>
        <inputFieldHeader
            v-if="label || placeholder || helpText"
            :help-text="helpText"
            :label="label"
            :placeholder="placeholder"
        />
        <div class="street-view">
            <div
                v-if="!disableSwitching"
                class="street-view__buttons mb-2"
            >
                <TheCheckbox
                    v-model="showStreetView"
                    label="Tilføj StreetView"
                />
            </div>
            <div
                v-show="showStreetView"
                ref="panoRef"
                class="street-view__panorama"
            ></div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.street-view {
    display: flex;
    flex-direction: column;

    & button {
        color: $dn-blue;

        border-color: $dn-blue;
        border-radius: 0.5rem !important;
        background-color: $dn-white;

        &.active {
            color: $dn-white;
            border-color: $dn-white;
            background-color: $dn-blue;
        }
    }

    &__panorama {
        width: 100%;
        height: 30rem;
    }

    &__buttons {
        display: flex;
        justify-content: space-between;
    }
}
</style>
